import { setSelectedLanguage } from '../../utils/variables';
import { t } from '../../utils/i18n';

const LANGUAGES = [
    { id: 'en', get name() { return t('language_english'); } },
    { id: 'es', get name() { return t('language_spanish'); } },
];

export class LanguageDropdown {
    constructor(config) {
        this.config = {
            containerSelector: config.containerSelector,
            inputId: config.inputId,
            dataForm: config.dataForm,
            wrapperId: config.wrapperId,
            downArrowId: config.downArrowId,
            upArrowId: config.upArrowId,
            onSelectionChange: config.onSelectionChange || null,
            initialValue: config.initialValue || null
        };

        this.hasUserInteracted = false;
        this.selectedLanguage = null;

        this.init();
    }

    init() {
        this.createHTML();
        this.setupEventListeners();
        this.populateOptions();
        this.setInitialValue();
        this.resetValidationState();
    }

    createHTML() {
        const container = document.querySelector(this.config.containerSelector);
        if (!container) {
            return;
        }

        container.innerHTML = this.getHTMLTemplate();
    }

    getHTMLTemplate() {
        return `
            <div data-form="${this.config.dataForm}" class="languageDropdown__wrapper dropdown form-control" id="${this.config.wrapperId}">
                <input id="${this.config.inputId}" autocomplete="off" placeholder="${t('select_language')}" readonly>
                <span class="down-arrow arrow" id="${this.config.downArrowId}">
                    <i class="ri-arrow-down-s-line"></i>
                </span>
                <span class="up-arrow arrow" id="${this.config.upArrowId}">
                    <i class="ri-arrow-up-s-line"></i>
                </span>
                <div class="languageDropdown__select">
                    <ul class="languages__select">
                    </ul>
                </div>
            </div>
        `;
    }

    populateOptions() {
        const list = this.getOptionsList();
        if (!list) return;

        list.innerHTML = '';
        LANGUAGES.forEach(lang => {
            const option = document.createElement('li');
            option.className = 'languageDropdown__option';
            option.setAttribute('data-language-id', lang.id);
            option.setAttribute('data-language', lang.name);
            option.textContent = lang.name;
            option.addEventListener('click', () => this.selectLanguage(lang.id, lang.name));
            list.appendChild(option);
        });
    }

    setupEventListeners() {
        const input = this.getInput();
        const downArrow = this.getDownArrow();
        const upArrow = this.getUpArrow();

        if (input) {
            input.addEventListener('click', () => this.toggle());
            input.addEventListener('keydown', (e) => this.handleKeydown(e));
        }

        if (downArrow) {
            downArrow.addEventListener('click', () => this.toggle());
        }

        if (upArrow) {
            upArrow.addEventListener('click', () => this.close());
        }

        document.addEventListener('click', (e) => this.handleOutsideClick(e));
    }

    handleKeydown(e) {
        switch (e.key) {
            case 'Enter':
            case ' ':
                e.preventDefault();
                this.toggle();
                break;
            case 'Escape':
                this.close();
                break;
            case 'ArrowDown':
                e.preventDefault();
                this.open();
                break;
            case 'ArrowUp':
                e.preventDefault();
                this.close();
                break;
        }
    }

    handleOutsideClick(e) {
        const wrapper = this.getWrapper();
        if (wrapper && !wrapper.contains(e.target)) {
            this.close();
        }
    }

    open() {
        const wrapper = this.getWrapper();
        if (wrapper) {
            wrapper.classList.add('open');
        }
    }

    close() {
        const wrapper = this.getWrapper();
        if (wrapper) {
            wrapper.classList.remove('open');
            this.syncUIState();
        }
    }

    toggle() {
        const wrapper = this.getWrapper();
        if (wrapper) {
            if (wrapper.classList.contains('open')) {
                this.close();
            } else {
                this.open();
            }
        }
    }

    selectLanguage(id, name) {
        this.hasUserInteracted = true;
        this.selectedLanguage = { id, name };

        const input = this.getInput();
        if (input) {
            input.value = name;
        }

        setSelectedLanguage(id);

        this.updateUIState(true);

        this.close();

        if (this.config.onSelectionChange) {
            this.config.onSelectionChange(id, name);
        }
    }

    clearSelection() {
        this.selectedLanguage = null;

        const input = this.getInput();
        if (input) {
            input.value = '';
        }

        setSelectedLanguage('');
        this.updateUIState(false);
    }

    updateUIState(isSelected) {
        const wrapper = this.getWrapper();
        const formItem = this.getFormItem();

        if (wrapper) {
            if (isSelected) {
                wrapper.classList.add('selected');
            } else {
                wrapper.classList.remove('selected');
            }
        }

        if (this.hasUserInteracted) {
            if (formItem) {
                if (isSelected) {
                    formItem.classList.remove('invalid');
                } else {
                    formItem.classList.add('invalid');
                }
            }
        }
    }

    syncUIState() {
        const input = this.getInput();
        if (!input) return;

        if (this.selectedLanguage) {
            // Re-derive the display name from the current language so it stays in sync
            // after the user picks a language (lang.name getter calls t() dynamically)
            const currentName = LANGUAGES.find(l => l.id === this.selectedLanguage.id)?.name || '';
            input.value = currentName;
            this.updateUIState(true);
        } else if (input.value.trim() === '') {
            const wrapper = this.getWrapper();
            if (wrapper) {
                wrapper.classList.remove('selected');
            }
        } else {
            this.clearSelection();
        }
    }

    isValidLanguage(languageName) {
        return LANGUAGES.some(lang => lang.name === languageName);
    }

    getWrapper() {
        return document.querySelector(`${this.config.containerSelector} .languageDropdown__wrapper`);
    }

    getInput() {
        return document.getElementById(this.config.inputId);
    }

    getDownArrow() {
        return document.getElementById(this.config.downArrowId);
    }

    getUpArrow() {
        return document.getElementById(this.config.upArrowId);
    }

    getOptionsList() {
        return document.querySelector(`${this.config.containerSelector} .languages__select`);
    }

    getFormItem() {
        const wrapper = this.getWrapper();
        return wrapper ? wrapper.closest('[data-form]') : null;
    }

    setInitialValue() {
        if (this.config.initialValue) {
            const language = LANGUAGES.find(lang =>
                lang.id === this.config.initialValue || lang.name === this.config.initialValue
            );
            if (language) {
                this.selectLanguage(language.id, language.name);
            }
        }
    }

    resetValidationState() {
        const formItem = this.getFormItem();
        if (formItem) {
            formItem.classList.remove('invalid');
        }
    }
}

export function loadLanguageDropdown() {
    return new LanguageDropdown({
        containerSelector: '.requestConsult__container .language__dropdown',
        inputId: 'languageConsult',
        dataForm: 'languageConsult',
        wrapperId: 'language-wrapper',
        downArrowId: 'languageConsultDownArrow',
        upArrowId: 'languageConsultUpArrow'
    });
}

export function loadLanguageDropdownVerify(leadLanguage = null) {
    return new LanguageDropdown({
        containerSelector: '.verifyInformation__container .language__dropdown-verify',
        inputId: 'languageVerify',
        dataForm: 'languageVerify',
        wrapperId: 'language-wrapper-verify',
        downArrowId: 'languageVerifyDownArrow',
        upArrowId: 'languageVerifyUpArrow',
        initialValue: leadLanguage
    });
}

export function isValidLanguageSelection(languageValue) {
    return LANGUAGES.some(lang => lang.name === languageValue);
}

export function getLanguageId(languageName) {
    const language = LANGUAGES.find(lang => lang.name === languageName);
    return language ? language.id : null;
}
